"use client";

import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { SplitText } from "gsap/SplitText";
import { CustomEase } from "gsap/CustomEase";
import { Link } from "@/i18n/routing";
import { useTranslations } from 'next-intl';

// Note: SplitText and CustomEase are Club GreenSock (paid) plugins.
// Make sure your GSAP license/install includes them, e.g.:
//   npm install gsap@npm:@gsap/business
// and that you're authorized to use the bonus plugins.
gsap.registerPlugin(SplitText, CustomEase);
export default function CarbonMenu() {
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const toggleBtnRef = useRef<HTMLButtonElement>(null);
  const timelineRef = useRef<gsap.core.Timeline | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Controls visibility of the overlay. Kept separate from isMenuOpen so the
  // colored background panels are only ever rendered while actually
  // animating open/closed — at true rest (closed, idle) they're
  // visibility:hidden, which removes the seam/hairline that otherwise shows
  // where the two rotated panels' edges meet at the center of the screen.
  const [showOverlay, setShowOverlay] = useState(false);

const t = useTranslations('Menu');
const te = useTranslations('Footer');


const mainLinks = [
  { label: t('blog'), href: "/blog" },
  { label: t('about'), href: "/about-us" },
  { label: t('contact'), href: "/contact-us" },
  { label: t('products'), href: "/product" },
]as const;

const productLinks = [
  { label: t('djellaba'), href: { pathname: "/p", query: { q: "jlb" } } },
  { label: t('mansouria'), href: { pathname: "/p", query: { q: "mnsr" } } },
  { label: t('kaftane'), href: { pathname: "/p", query: { q: "kftn" } } },
  { label: t('bedi\'iya'), href: { pathname: "/p", query: { q: "bdia" } } },
  { label: t('djabadour'), href: { pathname: "/p", query: { q: "djr" } } },
  { label: t('melaya'), href: { pathname: "/p", query: { q: "mlya" } } },
] as const;





const footerLegalLinks = [
  { label: te('Returns-and-Refunds'), href: "/returns-and-refunds" },
  { label: te('Privacy-Policy'), href: "/privacy-policy" },
  { label: te('Terms-and-Conditions'), href: "/terms-and-conditions" },
  { label: te('Cookies-Policy'), href: "/cookies-policy" },
]as const;

  useEffect(() => {
    if (!containerRef.current) return;

    const ctx = gsap.context(() => {
      CustomEase.create("hop", "0.85, 0, 0.15, 1");

      SplitText.create(".menu a, .menu p", {
        type: "lines",
        mask: "lines",
        linesClass: "line",
      });

      // Lines start pushed down inside their mask, then animate to y: 0
      gsap.set(".line", { y: "110%", position: "relative" });

      const tl = gsap.timeline({ paused: true });
      timelineRef.current = tl;

      tl.to(
        ".nav-toggle-btn .bar-1",
        { y: 3.25, rotation: 45, scaleX: 0.75, duration: 1, ease: "hop" },
        0
      )
        .to(
          ".nav-toggle-btn .bar-2",
          { y: -3.25, rotation: -45, scaleX: 0.75, duration: 1, ease: "hop" },
          0
        )
        .to(
          ".menu-bg-left-inner",
          { rotate: 0, duration: 1, ease: "hop" },
          0
        )
        .to(
          ".menu-bg-right-inner",
          { rotate: 0, duration: 1, ease: "hop" },
          0
        )
        .to(
          ".menu-items-col:nth-child(1) .line",
          { y: 0, duration: 0.75, ease: "power3.out", stagger: 0.1 },
          "0.6"
        )
        .to(
          ".menu-items-col:nth-child(2) .line",
          { y: 0, duration: 0.75, ease: "power3.out", stagger: 0.1 },
          "<"
        )
        .to(
          ".menu-footer .line",
          { y: 0, duration: 0.75, ease: "power3.out", stagger: 0.1 },
          "<"
        );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  const handleToggle = () => {
    const tl = timelineRef.current;
    if (!tl) return;

    if (isMenuOpen) {
      // Closing: keep the overlay visible until the reverse animation
      // fully finishes, then hide it so no resting-state seam is left.
      tl.eventCallback("onReverseComplete", () => setShowOverlay(false));
      tl.reverse();
    } else {
      // Opening: show the overlay right away, then play.
      setShowOverlay(true);
      tl.play();
    }
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <div ref={containerRef} className="contents">
      {/* Toggle button — place this directly inside your existing navbar */}
      <button
        ref={toggleBtnRef}
        onClick={handleToggle}
        aria-label="Toggle menu"
        aria-expanded={isMenuOpen}
        className="nav-toggle-btn relative z-[70] flex h-11 w-11 cursor-pointer p-2 flex-col items-center justify-center gap-[5px] rounded-full border border-yel hover:border-gold"
      >
        <span className="bar-1 h-[1.25px] w-full origin-center bg-current [will-change:transform]" />
        <span className="bar-2 h-[1.25px] w-full origin-center bg-current [will-change:transform]" />
      </button>

      {/* MENU OVERLAY */}
      <div
        ref={menuRef}
        style={{ visibility: showOverlay ? "visible" : "hidden" }}
        className={`menu fixed left-0 top-0 z-[60] h-[100svh] w-full overflow-hidden ${
          isMenuOpen ? "pointer-events-auto" : "pointer-events-none"
        }`}
      >
        {/* background split panels */}
        <div className="menu-bg pointer-events-none absolute h-full w-full">
          <div className="absolute left-0 h-full w-1/2 overflow-hidden max-[1000px]:w-full">
            <div
              className="menu-bg-left-inner absolute h-full w-full origin-right bg-b [will-change:transform]"
              style={{ transform: "rotate(180deg) scale(2, 2)" }}
            />
          </div>
          <div className="absolute right-0 h-full w-1/2 overflow-hidden max-[1000px]:hidden">
            <div
              className="menu-bg-right-inner absolute h-full w-full origin-left bg-a [will-change:transform]"
              style={{ transform: "rotate(-180deg) scale(2, 2)" }}
            />
          </div>
        </div>

        {/* menu items */}
        <div className="menu-items absolute flex h-full w-full max-[1000px]:flex-col-reverse max-[1000px]:gap-16 max-[1000px]:p-24 max-[1000px]:px-8">
          <div className="menu-items-col flex flex-1 flex-col items-center justify-center gap-8 max-[1000px]:items-start max-[1000px]:justify-start max-[1000px]:gap-2">
            {mainLinks.map(({ label, href }) => (
              <div className="menu-link" key={label}>
                <Link
                  href={href}
                  onClick={handleToggle}
                  className="text-[clamp(1.5rem,2.5vw,4rem)] italic font-medium uppercase leading-[1.1] text-yel no-underline"
                >
                  {label}
                </Link>
              </div>
            ))}
          </div>

          <div className="menu-items-col flex flex-1 flex-col font-playfair items-center justify-center gap-8 max-[1000px]:items-start max-[1000px]:justify-end max-[1000px]:gap-2">
            {productLinks.map(({ label, href }) => (
              <div className="menu-link" key={label}>
                <Link
                  href={href}
                  onClick={handleToggle}
                  className="font-serif text-[clamp(1.65rem,2.75vw,3rem)] font-medium leading-[1.2] text-yel no-underline"
                >
                  {label}
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* menu footer */}
        <div className="menu-footer absolute bottom-0 left-0 flex w-full items-end justify-between p-8 max-[1000px]:flex-col max-[1000px]:items-end max-[1000px]:gap-0 max-[1000px]:px-8">
          <div className="menu-footer-col flex gap-8 max-[1000px]:flex-col max-[1000px]:gap-0">
            {footerLegalLinks.map(({ label, href }) => (
              <Link
                key={label}
                href={href}
                onClick={handleToggle}
                className="text-[0.75rem] font-medium uppercase text-yel no-underline"
              >
                {label}
              </Link>
            ))}
          </div>
          <div className="menu-footer-col flex gap-8 max-[1000px]:flex-col max-[1000px]:gap-0">
            <p className="text-[0.75rem] font-medium uppercase leading-none text-yel">
              &copy; 2026 Boutique Chohra
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}